import { ITeam } from '@/data/models/Team'
import { IPlayer } from '@/data/models/Player'
import { IProtocol } from '@/data/models/Protocol'
import i18n from '@/plugins/i18n'

export enum EventType {
  TRAINING = 'training',
  MATCH = 'match',
  READINESS = 'readiness',
  TEST_SESSION = 'test_session',
  MEETING = 'meeting',
  ANALYSIS = 'analysis',
  VIDEO_ANALYSIS = 'video_analysis',
  MEDICAL_EXAM = 'medical_exam',
  THERAPY = 'therapy',
  REHAB = 'rehab',
  INDIVIDUAL_WORK = 'individual_work',
  MEAL = 'meal',
  REST = 'rest',
  TEAM_BUILDING = 'team_building',
  LIFTING = 'lifting',
  TRAVEL = 'travel',
  MASSAGE = 'massage',
  OTHER = 'other'
}

export enum TravelMeans {
  BUS = 'bus',
  AIRPLANE = 'airplane',
  CAR= 'car',
  TRAIN = 'train'
}

// Offline fallback palette. The authoritative colour comes from the API
// (event.color, resolved server-side). These distinct Vuetify colour tokens are used only when the API colour
// is absent; they approximate the backend per-type defaults (EventType::color()).
export const EventTypes = {
  [EventType.TRAINING]: {
    color: 'orange',
    get text() {
      return i18n.t('performance.advanced.training')
    },
    is_other: false
  },
  [EventType.MATCH]: {
    color: 'red',
    get text() {
      return i18n.t('performance.advanced.match')
    },
    is_other: false
  },
  [EventType.READINESS]: {
    color: 'blue-grey',
    get text() {
      return i18n.t('event_type.readiness')
    },
    is_other: true
  },
  [EventType.TEST_SESSION]: {
    color: 'blue',
    get text() {
      return i18n.t('event_type.test_session')
    },
    is_other: true
  },
  [EventType.MEETING]: {
    color: 'brown',
    get text() {
      return i18n.t('event_type.meeting')
    },
    is_other: true
  },
  [EventType.ANALYSIS]: {
    color: 'indigo',
    get text() {
      return i18n.t('event_type.analysis')
    },
    is_other: true
  },
  [EventType.VIDEO_ANALYSIS]: {
    color: 'deep-purple',
    get text() {
      return i18n.t('event_type.video_analysis')
    },
    is_other: true
  },
  [EventType.MEDICAL_EXAM]: {
    color: 'pink',
    get text() {
      return i18n.t('event_type.medical_exam')
    },
    is_other: true
  },
  [EventType.THERAPY]: {
    color: 'teal',
    get text() {
      return i18n.t('event_type.therapy')
    },
    is_other: true
  },
  [EventType.REHAB]: {
    color: 'cyan',
    get text() {
      return i18n.t('event_type.rehab')
    },
    is_other: true
  },
  [EventType.INDIVIDUAL_WORK]: {
    color: 'purple',
    get text() {
      return i18n.t('event_type.individual_work')
    },
    is_other: true,
    default_exclude: true
  },
  [EventType.MEAL]: {
    color: 'amber',
    get text() {
      return i18n.t('event_type.meal')
    },
    is_other: true
  },
  [EventType.REST]: {
    color: 'grey',
    get text() {
      return i18n.t('event_type.rest')
    },
    is_other: true
  },
  [EventType.TEAM_BUILDING]: {
    color: 'light-green',
    get text() {
      return i18n.t('event_type.team_building')
    },
    is_other: true
  },
  [EventType.LIFTING]: {
    color: 'deep-purple',
    get text() {
      return i18n.t('event_type.lifting')
    },
    is_other: true
  },
  [EventType.TRAVEL]: {
    color: 'lime',
    get text() {
      return i18n.t('event_type.travel')
    },
    is_other: true
  },
  [EventType.MASSAGE]: {
    color: 'green',
    get text() {
      return i18n.t('event_type.massage')
    },
    is_other: true
  },
  [EventType.OTHER]: {
    color: 'blue-grey lighten-2',
    get text() {
      return i18n.t('event_type.other')
    },
    is_other: true
  }
}

// Merge the backend-served event types (the single source of truth for which
// types exist, their colour, "other" grouping and translated label) into the
// EventTypes map. The hardcoded entries above remain only as an offline
// fallback; once this runs at app init every consumer reads backend data, so a
// new type added on the backend appears everywhere without a client change.
export function applyBackendEventTypes(
  list: Array<{ value: string; is_other: boolean; color: string; label: string }>
): void {
  if (!Array.isArray(list)) return
  const map = EventTypes as Record<string, { color: string; text: string; is_other: boolean; default_exclude?: boolean }>
  list.forEach(t => {
    map[t.value] = {
      ...map[t.value],
      color: t.color,
      text: t.label,
      is_other: t.is_other
    }
  })
}

// Shared preset colour palette used by every colour picker (event override,
// team colour, admin type defaults) on web AND mobile. Must stay in sync with
// the mobile EventColorPickerWidget.palette and the backend Event::COLOR_PALETTE.
export const EVENT_COLOR_PALETTE = [
  '#FF9800', '#F44336', '#2196F3', '#4CAF50', '#8E24AA', '#009688',
  '#00BCD4', '#3F51B5', '#795548', '#E91E63', '#607D8B', '#7CB342',
  '#FFB300', '#26A69A', '#9E9E9E', '#455A64', '#E53935', '#D81B60',
  '#AB47BC', '#5E35B1', '#1E88E5', '#039BE5', '#00ACC1', '#00897B',
  '#43A047', '#C0CA33', '#FDD835', '#FB8C00', '#F4511E', '#6D4C41'
]

export const TravelMeansList = {
  [TravelMeans.BUS]: {
    get text() {
      return i18n.t('event_means_of_travel.bus')
    }
  },
  [TravelMeans.AIRPLANE]: {
    get text() {
      return i18n.t('event_means_of_travel.airplane')
    }
  },
  [TravelMeans.CAR]: {
    get text() {
      return i18n.t('event_means_of_travel.car')
    }
  },
  [TravelMeans.TRAIN]: {
    get text() {
      return i18n.t('event_means_of_travel.train')
    }
  }
}

export interface IEvent {
  id: number;
  start_at: string;
  end_at: string;
  players: IPlayer[];
  event_type: string;
  color?: string | null;
  teams: ITeam[];
  protocol_id: number;
  protocol?: IProtocol;
  match_score?: number[] | null;
  is_home?: boolean | null;
  opponent?: { id: number; name: string } | null;
  match_opponent_id?: number | null;
}
